/**
 * On-device voice fingerprint for desktop Aria: spectral profile (FFT band shape).
 * Not biometric-grade; good enough to ignore many other speakers / TV in the same room.
 *
 * After each successful, gated utterance, we blend the new fingerprint into the stored
 * reference (EMA) so the model adapts to the user over time (same room, mic drift, tone).
 *
 * Other voices: never sent to Whisper — filtered locally before transcribe. Aria does not
 * "read" background speech unless you disable "Only respond to my voice."
 */

export const VOICE_PROFILE_STORAGE_KEY = 'aria_voice_profile_v1';
/** Cosine similarity threshold; tune if too strict/loose (0.72–0.88 typical).
 *  0.76 blocks most background voices while still recognising the user's natural variation. */
export const DEFAULT_VOICE_MATCH_THRESHOLD = 0.76;
export const VOICE_ENROLL_MS = 5000;
export const MIN_FRAMES_FOR_MATCH = 8;

/** EMA blend: higher momentum = slower change. Vectors must be same length (L2-normalized). */
export function blendEMA(oldRef, newVec, momentum) {
  if (!oldRef || !newVec || oldRef.length !== newVec.length) return newVec;
  const n = oldRef.length;
  const out = new Float32Array(n);
  const m = Math.max(0, Math.min(0.999, momentum));
  for (let i = 0; i < n; i++) {
    out[i] = m * oldRef[i] + (1 - m) * newVec[i];
  }
  let norm = 0;
  for (let i = 0; i < n; i++) norm += out[i] * out[i];
  norm = Math.sqrt(norm) || 1;
  for (let i = 0; i < n; i++) out[i] /= norm;
  return out;
}

/** More samples → slower drift (keeps stable after long use). */
export function momentumForAdaptationCount(count) {
  const c = Math.max(0, Math.min(80, count || 0));
  const base = 0.88;
  const cap = 0.97;
  return Math.min(cap, base + c * 0.00115);
}

export function addBinsToSum(sum, bins) {
  for (let i = 0; i < bins.length; i++) {
    sum[i] += bins[i] / 255;
  }
}

/** Average accumulated bins → shape vector, L2-normalized (for cosine vs reference). */
export function sumToMeanVector(sum, count) {
  if (count < 1 || !sum || sum.length === 0) return null;
  const n = sum.length;
  const f = new Float32Array(n);
  for (let i = 0; i < n; i++) f[i] = sum[i] / count;
  let s = 0;
  for (let i = 0; i < n; i++) s += f[i];
  if (s > 1e-6) {
    for (let i = 0; i < n; i++) f[i] /= s;
  }
  let norm = 0;
  for (let i = 0; i < n; i++) norm += f[i] * f[i];
  norm = Math.sqrt(norm) || 1;
  for (let i = 0; i < n; i++) f[i] /= norm;
  return f;
}

export function cosineSimilarity(a, b) {
  if (!a || !b || a.length !== b.length) return 0;
  let d = 0;
  for (let i = 0; i < a.length; i++) d += a[i] * b[i];
  return d;
}

function parseStoredProfile(raw) {
  if (!raw) return null;
  const o = JSON.parse(raw);
  if (!o || !Array.isArray(o.embedding)) return null;
  const adaptationCount = typeof o.adaptationCount === 'number' && o.adaptationCount >= 0
    ? o.adaptationCount
    : 1;
  return {
    embedding: Float32Array.from(o.embedding),
    strict: o.strict !== false,
    enrolledAt: o.enrolledAt || null,
    adaptationCount,
  };
}

export function loadVoiceProfile() {
  try {
    const raw = localStorage.getItem(VOICE_PROFILE_STORAGE_KEY);
    if (!raw) return null;
    return parseStoredProfile(raw);
  } catch {
    return null;
  }
}

/**
 * @param {Float32Array} embedding
 * @param {boolean} strict
 * @param {{ enrolledAt?: string|null, adaptationCount?: number }} [meta]
 */
export function saveVoiceProfile(embedding, strict = true, meta = {}) {
  let prev = null;
  try {
    const raw = localStorage.getItem(VOICE_PROFILE_STORAGE_KEY);
    if (raw) prev = parseStoredProfile(raw);
  } catch (_) {}
  const now = new Date().toISOString();
  const data = {
    version: 2,
    embedding: Array.from(embedding),
    strict,
    enrolledAt: meta.enrolledAt !== undefined && meta.enrolledAt !== null ? meta.enrolledAt : (prev?.enrolledAt || now),
    adaptationCount: meta.adaptationCount != null ? meta.adaptationCount : (prev?.adaptationCount ?? 1),
  };
  localStorage.setItem(VOICE_PROFILE_STORAGE_KEY, JSON.stringify(data));
}

export function clearVoiceProfile() {
  try {
    localStorage.removeItem(VOICE_PROFILE_STORAGE_KEY);
  } catch (_) {}
}

// ═══════════════════════════════════════════════════════════════════════════════
// LAYER 1 ENHANCED VOICE GATE — 20-dimensional speaker embedding
// ═══════════════════════════════════════════════════════════════════════════════

// Bark-scale perceptual frequency bands (Hz boundaries)
const BARK_BANDS = [
  [0, 300],     // Sub-bass / fundamental
  [300, 600],   // Low formants
  [600, 1000],  // First formant region
  [1000, 1600], // Second formant region
  [1600, 2400], // Upper formants
  [2400, 3400], // Speech clarity band
  [3400, 5000], // Fricatives / sibilants
  [5000, 8000], // High harmonics
];

// Match history storage key (for adaptive threshold)
const MATCH_HISTORY_KEY = 'aria_voice_match_history';
const MATCH_HISTORY_MAX = 30;

/**
 * Estimate fundamental frequency (F0) using autocorrelation on time-domain data.
 * Returns { f0, confidence } where f0 is in Hz and confidence is 0-1.
 */
export function estimateF0(timeDomainData, sampleRate) {
  if (!timeDomainData || timeDomainData.length < 128 || !sampleRate) {
    return { f0: 0, confidence: 0 };
  }

  const n = timeDomainData.length;
  // Convert Uint8Array (0-255) to float (-1 to 1)
  const signal = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    signal[i] = (timeDomainData[i] - 128) / 128;
  }

  // Autocorrelation for lag range corresponding to 50-400 Hz (covers male + female + child)
  const minLag = Math.floor(sampleRate / 400); // ~400 Hz upper bound
  const maxLag = Math.ceil(sampleRate / 50);   // ~50 Hz lower bound
  const safeLag = Math.min(maxLag, Math.floor(n / 2));

  let bestLag = 0;
  let bestCorr = -1;
  let energy = 0;

  // Compute signal energy
  for (let i = 0; i < n; i++) energy += signal[i] * signal[i];
  if (energy < 0.001) return { f0: 0, confidence: 0 }; // silence

  for (let lag = minLag; lag <= safeLag; lag++) {
    let corr = 0;
    for (let i = 0; i < n - lag; i++) {
      corr += signal[i] * signal[i + lag];
    }
    corr /= (n - lag); // Normalize by overlap length
    if (corr > bestCorr) {
      bestCorr = corr;
      bestLag = lag;
    }
  }

  if (bestLag === 0 || bestCorr < 0.01) return { f0: 0, confidence: 0 };

  const f0 = sampleRate / bestLag;
  const confidence = Math.min(1, Math.max(0, bestCorr / (energy / n)));
  return { f0, confidence };
}

/**
 * Compute energy in 8 Bark-scale frequency bands from FFT data.
 * Returns Float32Array(8) with normalized energy per band.
 */
export function computeBarkBandEnergies(freqData, sampleRate, binCount) {
  if (!freqData || !sampleRate || !binCount) return new Float32Array(8);

  const hzPerBin = sampleRate / (binCount * 2);
  const energies = new Float32Array(8);
  let totalEnergy = 0;

  for (let b = 0; b < BARK_BANDS.length; b++) {
    const [lo, hi] = BARK_BANDS[b];
    const startBin = Math.max(0, Math.floor(lo / hzPerBin));
    const endBin = Math.min(binCount - 1, Math.ceil(hi / hzPerBin));
    let bandEnergy = 0;
    for (let i = startBin; i <= endBin; i++) {
      const val = freqData[i] / 255; // Normalize 0-1
      bandEnergy += val * val;
    }
    energies[b] = bandEnergy;
    totalEnergy += bandEnergy;
  }

  // Normalize
  if (totalEnergy > 0) {
    for (let i = 0; i < 8; i++) energies[i] /= totalEnergy;
  }
  return energies;
}

/**
 * Compute spectral slopes between adjacent Bark bands.
 * Returns Float32Array(7) — ratios capture voice timbre shape.
 */
export function computeSpectralSlopes(bandEnergies) {
  const slopes = new Float32Array(7);
  for (let i = 0; i < 7; i++) {
    const denom = bandEnergies[i] || 0.001;
    slopes[i] = (bandEnergies[i + 1] || 0) / denom;
  }
  return slopes;
}

/**
 * Count zero crossings in time-domain data as a speaking cadence proxy.
 * Returns crossings per sample (normalized).
 */
export function computeZeroCrossings(timeDomainData) {
  if (!timeDomainData || timeDomainData.length < 2) return 0;
  let crossings = 0;
  for (let i = 1; i < timeDomainData.length; i++) {
    const prev = timeDomainData[i - 1] - 128;
    const curr = timeDomainData[i] - 128;
    if ((prev >= 0 && curr < 0) || (prev < 0 && curr >= 0)) {
      crossings++;
    }
  }
  return crossings / timeDomainData.length;
}

/**
 * Build a 20-dimensional enhanced speaker embedding from an AnalyserNode.
 * Combines: 8 band energies + 7 spectral slopes + F0 mean + F0 variance
 *         + zero-crossing mean + zero-crossing variance = 20 features
 *
 * Call this repeatedly during recording and accumulate, then average.
 */
export function buildEnhancedFrame(analyser, sampleRate) {
  if (!analyser || !sampleRate) return null;

  const binCount = analyser.frequencyBinCount;
  const freqData = new Uint8Array(binCount);
  analyser.getByteFrequencyData(freqData);

  const timeDomain = new Uint8Array(analyser.fftSize);
  analyser.getByteTimeDomainData(timeDomain);

  const bandEnergies = computeBarkBandEnergies(freqData, sampleRate, binCount);
  const slopes = computeSpectralSlopes(bandEnergies);
  const { f0, confidence: f0Conf } = estimateF0(timeDomain, sampleRate);
  const zcr = computeZeroCrossings(timeDomain);

  return { bandEnergies, slopes, f0, f0Conf, zcr };
}

/**
 * Accumulator for enhanced embedding frames.
 * Call addFrame() during recording, then getEmbedding() to produce the final 20-d vector.
 */
export class EnhancedEmbeddingAccumulator {
  constructor() {
    this.bandSums = new Float32Array(8);
    this.slopeSums = new Float32Array(7);
    this.f0Values = [];
    this.zcrValues = [];
    this.frameCount = 0;
  }

  addFrame(frame) {
    if (!frame) return;
    for (let i = 0; i < 8; i++) this.bandSums[i] += frame.bandEnergies[i];
    for (let i = 0; i < 7; i++) this.slopeSums[i] += frame.slopes[i];
    if (frame.f0 > 0 && frame.f0Conf > 0.3) {
      this.f0Values.push(frame.f0);
    }
    this.zcrValues.push(frame.zcr);
    this.frameCount++;
  }

  getEmbedding() {
    if (this.frameCount < 3) return null;

    const embed = new Float32Array(20);

    // [0-7] Band energies (averaged)
    for (let i = 0; i < 8; i++) embed[i] = this.bandSums[i] / this.frameCount;

    // [8-14] Spectral slopes (averaged)
    for (let i = 0; i < 7; i++) embed[8 + i] = this.slopeSums[i] / this.frameCount;

    // [15] F0 mean
    const f0Mean = this.f0Values.length > 0
      ? this.f0Values.reduce((a, b) => a + b, 0) / this.f0Values.length
      : 0;
    embed[15] = f0Mean / 400; // Normalize to ~0-1 range (400 Hz max expected)

    // [16] F0 variance (normalized)
    if (this.f0Values.length > 1) {
      let f0Var = 0;
      for (const f of this.f0Values) f0Var += (f - f0Mean) ** 2;
      f0Var /= this.f0Values.length;
      embed[16] = Math.sqrt(f0Var) / 100; // Normalize std dev
    }

    // [17] Zero-crossing rate mean
    const zcrMean = this.zcrValues.reduce((a, b) => a + b, 0) / this.zcrValues.length;
    embed[17] = zcrMean;

    // [18] Zero-crossing rate variance
    if (this.zcrValues.length > 1) {
      let zcrVar = 0;
      for (const z of this.zcrValues) zcrVar += (z - zcrMean) ** 2;
      zcrVar /= this.zcrValues.length;
      embed[18] = Math.sqrt(zcrVar);
    }

    // [19] Spectral flatness (Wiener entropy) — helps detect compressed/TV audio
    const bandMean = embed.slice(0, 8).reduce((a, b) => a + b, 0) / 8;
    let logSum = 0;
    for (let i = 0; i < 8; i++) logSum += Math.log(embed[i] + 1e-8);
    const geoMean = Math.exp(logSum / 8);
    embed[19] = bandMean > 0 ? geoMean / bandMean : 0;

    // L2 normalize the full vector
    let norm = 0;
    for (let i = 0; i < 20; i++) norm += embed[i] * embed[i];
    norm = Math.sqrt(norm) || 1;
    for (let i = 0; i < 20; i++) embed[i] /= norm;

    return embed;
  }
}

/**
 * Compute an adaptive voice match threshold based on recent match history.
 * After 10+ matches, uses mean - 2*stddev for a personalized threshold.
 * Falls back to DEFAULT_VOICE_MATCH_THRESHOLD if insufficient data.
 */
export function computeAdaptiveThreshold(matchHistory) {
  if (!matchHistory || matchHistory.length < 10) return DEFAULT_VOICE_MATCH_THRESHOLD;

  const scores = matchHistory.slice(-MATCH_HISTORY_MAX);
  const mean = scores.reduce((a, b) => a + b, 0) / scores.length;
  let variance = 0;
  for (const s of scores) variance += (s - mean) ** 2;
  variance /= scores.length;
  const stddev = Math.sqrt(variance);

  // 3σ tolerance + lower cap — 0.92 was rejecting legit voice at ~0.90.
  const adaptive = mean - 3 * stddev;
  return Math.max(0.6, Math.min(0.88, adaptive));
}

/**
 * Save a match score to the adaptive threshold history.
 */
export function recordMatchScore(score) {
  try {
    const raw = localStorage.getItem(MATCH_HISTORY_KEY);
    const history = raw ? JSON.parse(raw) : [];
    history.push(score);
    while (history.length > MATCH_HISTORY_MAX) history.shift();
    localStorage.setItem(MATCH_HISTORY_KEY, JSON.stringify(history));
    return history;
  } catch {
    return [];
  }
}

/**
 * Load the match score history for adaptive threshold computation.
 */
export function loadMatchHistory() {
  try {
    const raw = localStorage.getItem(MATCH_HISTORY_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}
